import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { HeaderDashboard } from '@/components/shared/header-dashboard';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import { useDeleteUserMeMutation } from '@/hooks/mutations/use-delete-users-me.mutation';
import { toast } from 'sonner';
import { useAuth } from '@/context/auth-context';
import { UserX } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { notifyError } from '@/lib/notify';

export const Route = createFileRoute(
  '/(consumer)/_authenticated/request-delete-account'
)({
  component: RequestDeleteAccountPage,
});

const DELETE_REASONS = [
  'I no longer need cleaning services',
  'I had a bad experience with a cleaner',
  'I found another cleaning company',
  'Prices are too high',
  'I had issues with the app or website',
  'Other',
] as const;

function RequestDeleteAccountPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState('');
  const deleteAccountMutation = useDeleteUserMeMutation();

  const handleSubmit = async () => {
    if (!selectedReason) {
      notifyError('Please select a reason for leaving');
      return;
    }

    const reason = selectedReason === 'Other' ? customReason : selectedReason;

    if (selectedReason === 'Other' && !customReason.trim()) {
      notifyError('Please provide a reason');
      return;
    }

    try {
      await deleteAccountMutation.mutateAsync({
        payload: {
          reason,
        },
      });
      toast.success('Account deletion request submitted successfully');
      // Logout user after successful deletion request
      await logout();
      navigate({ to: '/login' });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete account:', error);
      toast.error('Failed to submit deletion request. Please try again.');
    }
  };

  return (
    <div>
      <HeaderDashboard>
        <HeaderDashboard.Title>Request Delete Account</HeaderDashboard.Title>
      </HeaderDashboard>

      <div className="flex flex-col items-center gap-6 mt-8 max-w-2xl mx-auto">
        {/* Icon */}
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-2 border-gray-300 flex items-center justify-center bg-white">
            <UserX className="w-12 h-12 text-gray-600" />
          </div>
          <div className="absolute -top-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-lg font-bold">×</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-center">
          Request Delete Account Receive
        </h1>

        <Card className="w-full">
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Question */}
              <div>
                <h2 className="text-lg font-semibold mb-4">
                  Why did you decide to leave Itria Home?
                </h2>

                {/* Radio Group */}
                <RadioGroup
                  value={selectedReason}
                  onValueChange={setSelectedReason}
                  className="space-y-3"
                >
                  {DELETE_REASONS.map(reason => (
                    <div key={reason} className="flex items-center space-x-2">
                      <RadioGroupItem value={reason} id={reason} />
                      <Label
                        htmlFor={reason}
                        className="cursor-pointer font-normal text-sm"
                      >
                        {reason}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Text Area */}
              <div className="space-y-2">
                <Textarea
                  placeholder="Write your reason here"
                  value={customReason}
                  onChange={e => setCustomReason(e.target.value)}
                  className="min-h-32 resize-none"
                  disabled={selectedReason !== 'Other'}
                />
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={deleteAccountMutation.isPending}
                className="w-full bg-gradient-to-r from-[#FF783A] to-[#FE5000] hover:opacity-90"
                size="lg"
              >
                {deleteAccountMutation.isPending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
